import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import { Pencil, Plus, Search, X } from 'lucide-react'
import { AppShell, Crumb, HomeCrumb } from '../components/AppShell'
import { EmptyState, Eyebrow, Mono, Surface } from '../components/primitives'
import { Segmented } from '../components/Segmented'
import { SkeletonCards } from '../components/Skeleton'
import { CountryPicker } from '../components/CountryPicker'
import { FoPicker, SupplierPicker, type RecordOption } from '../components/pickers'
import { useCountries, useDirectoryMutations, useFarmerOrganisations, useFarmers, useSession, useSuppliers } from '../api/hooks'
import type { Farmer } from '../api/types'
import { num } from '../lib/format'
import { tokens } from '../theme'

type Tab = 'suppliers' | 'fos' | 'farmers'

/**
 * The directory: who the plots belong to.
 *
 * Suppliers, the farmer organisations that deliver to them and the farmers
 * in those — records that uploads create and link as plots arrive, kept here
 * by hand: renamed (on every plot at once), given their codes, linked.
 */
export function Directory() {
  const [params, setParams] = useSearchParams()
  const tab = (params.get('tab') as Tab) || 'suppliers'
  const [q, setQ] = useState('')
  const [error, setError] = useState<string | null>(null)

  return (
    <AppShell crumbs={[<HomeCrumb key="a" />, <Crumb key="b">Directory</Crumb>]}>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 7 }}>
        <Eyebrow sx={{ mb: 2 }}>Supply chain</Eyebrow>
        <Typography variant="h1" sx={{ mb: 2 }}>Directory</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5, maxWidth: 680 }}>
          Who your plots belong to. Uploads add suppliers, farmer organisations and farmers as
          they arrive; rename them here and every plot follows.
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 4 }}>
          <Segmented
            ariaLabel="Directory section"
            value={tab}
            onChange={(v) => { setParams({ tab: v }); setQ('') }}
            options={[
              { value: 'suppliers', label: 'Suppliers' },
              { value: 'fos', label: 'Farmer organisations' },
              { value: 'farmers', label: 'Farmers' },
            ]}
          />
          <TextField
            size="small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tab === 'farmers' ? 'Search Farmer_ID or name' : tab === 'fos' ? 'Search name or FO_ID' : 'Search suppliers'}
            inputProps={{ 'aria-label': 'Search the directory' }}
            InputProps={{ startAdornment: <Search size={14} strokeWidth={1.5} style={{ marginRight: 8, opacity: 0.6 }} /> }}
            sx={{ width: 300 }}
          />
        </Box>

        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>{error}</Alert>}

        {tab === 'suppliers' && <Suppliers q={q} onError={setError} />}
        {tab === 'fos' && <FarmerOrganisations q={q} onError={setError} />}
        {tab === 'farmers' && <Farmers q={q} onError={setError} />}
      </Box>
    </AppShell>
  )
}

const row = (columns: string) => ({
  display: 'grid', gridTemplateColumns: columns, alignItems: 'center', gap: 4, px: 5, py: 3,
  borderBottom: `1px solid ${tokens.color.ink200}`, '&:last-of-type': { borderBottom: 0 },
})

/** A value that turns into a field on "edit". */
function Editable({
  value, onSave, mono, placeholder, ariaLabel,
}: {
  value: string | null
  onSave: (next: string) => Promise<unknown>
  mono?: boolean
  placeholder?: string
  ariaLabel: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const [saving, setSaving] = useState(false)
  if (!editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, '&:hover .edit': { opacity: 1 } }}>
        <Box component={mono ? Mono : 'span'} sx={{ fontSize: mono ? 13 : 14, color: value ? 'text.primary' : 'text.disabled', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || placeholder || '—'}
        </Box>
        <IconButton className="edit" size="small" aria-label={`Edit ${ariaLabel}`} sx={{ opacity: 0.4 }}
          onClick={() => { setDraft(value ?? ''); setEditing(true) }}>
          <Pencil size={12} strokeWidth={1.5} />
        </IconButton>
      </Box>
    )
  }
  const save = async () => {
    setSaving(true)
    try {
      await onSave(draft.trim())
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <TextField
        size="small" autoFocus value={draft} disabled={saving}
        inputProps={{ 'aria-label': ariaLabel }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
        sx={{ flex: 1, '& input': { fontFamily: mono ? tokens.font.mono : undefined, fontSize: 13 } }}
      />
      <Button size="small" disabled={saving} onClick={save}>Save</Button>
      <Button size="small" variant="text" disabled={saving} onClick={() => setEditing(false)}>Cancel</Button>
    </Box>
  )
}

const failed = (onError: (m: string) => void) => (err: unknown) => {
  onError((err as Error).message)
  throw err
}

// --- Suppliers ---------------------------------------------------------------

function Suppliers({ q, onError }: { q: string; onError: (m: string) => void }) {
  const { data = [], isLoading } = useSuppliers()
  const { data: countries = [] } = useCountries()
  const { createSupplier, renameSupplier } = useDirectoryMutations()
  // Kept in step from the organisation's own list, by its ids: read-only here.
  const synced = useSession().data?.directory === 'synced'
  const [name, setName] = useState('')
  const [country, setCountry] = useState('CI')
  const shown = data.filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()))
  const cols = 'minmax(0,2fr) 120px 120px'

  return (
    <>
      {!synced && <AddRow
        label="Add supplier"
        disabled={!name.trim() || createSupplier.isPending}
        onAdd={() => createSupplier.mutateAsync({ name: name.trim(), country_code: country }).then(() => setName('')).catch(failed(onError))}
      >
        <TextField size="small" value={name} onChange={(e) => setName(e.target.value)} placeholder="Supplier name"
          inputProps={{ 'aria-label': 'New supplier name' }} sx={{ flex: 1 }} />
        <Box sx={{ width: 220 }}><CountryPicker countries={countries} value={country} onChange={setCountry} label="Country" /></Box>
      </AddRow>}
      {synced && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Suppliers come from your own list and are kept in step with it. Add or rename them there.
        </Typography>
      )}
      <Surface sx={{ p: 0, mb: 4 }}>
        <Box sx={{ ...row(cols), color: 'text.secondary' }}>
          <Eyebrow>Supplier</Eyebrow><Eyebrow>Country</Eyebrow><Eyebrow>Regions</Eyebrow>
        </Box>
        {isLoading && <SkeletonCards columns={cols} />}
        {shown.map((s) => (
          <Box key={s.id} sx={row(cols)}>
            <Box sx={{ minWidth: 0 }}>
              {synced ? (
                <Typography variant="body2" noWrap>{s.name}</Typography>
              ) : (
                <Editable value={s.name} ariaLabel={`name of ${s.name}`}
                  onSave={(next) => renameSupplier.mutateAsync({ id: s.id, name: next }).catch(failed(onError))} />
              )}
              {/* Kept in step from your own list, by your id. */}
              {s.external_ref && <Mono sx={{ fontSize: 11, color: 'text.secondary' }}>{s.external_ref}</Mono>}
            </Box>
            <Mono sx={{ fontSize: 13 }}>{s.country_code}</Mono>
            <Typography variant="body2">{num(s.region_count)}</Typography>
          </Box>
        ))}
        {!isLoading && !shown.length && <Box sx={{ p: 5 }}><EmptyState title="No suppliers" body={q ? 'Nothing matches that search.' : 'Suppliers are added as files naming them are uploaded, or here.'} /></Box>}
      </Surface>
    </>
  )
}

// --- Farmer organisations ------------------------------------------------------

function FarmerOrganisations({ q, onError }: { q: string; onError: (m: string) => void }) {
  const { data = [], isLoading } = useFarmerOrganisations({ q, limit: 200 })
  const { createFo, updateFo } = useDirectoryMutations()
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [supplier, setSupplier] = useState<RecordOption | null>(null)
  const cols = 'minmax(0,2fr) minmax(0,1fr) 100px 100px'

  return (
    <>
      <AddRow
        label="Add farmer organisation"
        disabled={!name.trim() || createFo.isPending}
        onAdd={() => createFo.mutateAsync({ name: name.trim(), code: code.trim() || null, ...(supplier ? { supplier_id: supplier.id } : {}) })
          .then(() => { setName(''); setCode('') }).catch(failed(onError))}
      >
        <TextField size="small" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name"
          inputProps={{ 'aria-label': 'New farmer organisation name' }} sx={{ flex: 1 }} />
        <TextField size="small" value={code} onChange={(e) => setCode(e.target.value)} placeholder="FO_ID (optional)"
          inputProps={{ 'aria-label': 'New FO_ID' }} sx={{ width: 180 }} />
        <Box sx={{ width: 240 }}><SupplierPicker value={supplier} onChange={setSupplier} countryCode={null} /></Box>
      </AddRow>
      <Surface sx={{ p: 0, mb: 4 }}>
        <Box sx={{ ...row(cols) }}>
          <Eyebrow>Farmer organisation</Eyebrow><Eyebrow>FO_ID</Eyebrow><Eyebrow>Farmers</Eyebrow><Eyebrow>Plots</Eyebrow>
        </Box>
        {isLoading && <SkeletonCards columns={cols} />}
        {data.map((o) => (
          <Box key={o.id} sx={row(cols)}>
            <Editable value={o.name} ariaLabel={`name of ${o.name}`}
              onSave={(next) => updateFo.mutateAsync({ id: o.id, name: next }).catch(failed(onError))} />
            <Editable value={o.code} mono placeholder="No FO_ID" ariaLabel={`FO_ID of ${o.name}`}
              onSave={(next) => updateFo.mutateAsync({ id: o.id, code: next || null }).catch(failed(onError))} />
            <Typography variant="body2">{num(o.farmer_count)}</Typography>
            <Typography variant="body2">{num(o.plot_count)}</Typography>
          </Box>
        ))}
        {!isLoading && !data.length && <Box sx={{ p: 5 }}><EmptyState title="No farmer organisations" body={q ? 'Nothing matches that search.' : 'They are added as files naming them are uploaded, or here.'} /></Box>}
      </Surface>
    </>
  )
}

// --- Farmers -------------------------------------------------------------------

const PAGE = 50

function Farmers({ q, onError }: { q: string; onError: (m: string) => void }) {
  const [limit, setLimit] = useState(PAGE)
  const { data = [], isLoading, isFetching } = useFarmers({ q, limit })
  const { createFarmer, updateFarmer } = useDirectoryMutations()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const cols = 'minmax(0,1fr) minmax(0,1.2fr) 80px minmax(0,2.4fr)'

  return (
    <>
      <AddRow
        label="Add farmer"
        disabled={(!code.trim() && !name.trim()) || createFarmer.isPending}
        onAdd={() => createFarmer.mutateAsync({ code: code.trim() || null, name: name.trim() || null })
          .then(() => { setCode(''); setName('') }).catch(failed(onError))}
      >
        <TextField size="small" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Farmer_ID"
          inputProps={{ 'aria-label': 'New Farmer_ID' }} sx={{ width: 200 }} />
        <TextField size="small" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)"
          inputProps={{ 'aria-label': 'New farmer name' }} sx={{ flex: 1 }} />
      </AddRow>
      <Surface sx={{ p: 0, mb: 4 }}>
        <Box sx={{ ...row(cols) }}>
          <Eyebrow>Farmer_ID</Eyebrow><Eyebrow>Name</Eyebrow><Eyebrow>Plots</Eyebrow><Eyebrow>Sells through</Eyebrow>
        </Box>
        {isLoading && <SkeletonCards columns={cols} />}
        {data.map((f) => (
          <Box key={f.id} sx={row(cols)}>
            <Editable value={f.code} mono placeholder="No Farmer_ID" ariaLabel={`Farmer_ID of ${f.code ?? f.name}`}
              onSave={(next) => updateFarmer.mutateAsync({ id: f.id, code: next || null }).catch(failed(onError))} />
            <Editable value={f.name} placeholder="No name" ariaLabel={`name of ${f.code ?? f.name}`}
              onSave={(next) => updateFarmer.mutateAsync({ id: f.id, name: next || null }).catch(failed(onError))} />
            <Typography variant="body2">{num(f.plot_count)}</Typography>
            <FarmerLinks farmer={f} onError={onError} />
          </Box>
        ))}
        {!isLoading && !data.length && <Box sx={{ p: 5 }}><EmptyState title="No farmers" body={q ? 'Nothing matches that search.' : 'Farmers are added as files with a Farmer_ID are uploaded, or here.'} /></Box>}
      </Surface>
      {data.length >= limit && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Button variant="outlined" size="small" disabled={isFetching} onClick={() => setLimit((l) => l + PAGE)}>Show more</Button>
        </Box>
      )}
    </>
  )
}

/** The channels a farmer sells through, each removable, and a way to add one. */
function FarmerLinks({ farmer, onError }: { farmer: Farmer; onError: (m: string) => void }) {
  const { linkFarmer, unlinkFarmer } = useDirectoryMutations()
  const [adding, setAdding] = useState(false)
  const [supplier, setSupplier] = useState<RecordOption | null>(null)
  const [fo, setFo] = useState<RecordOption | null>(null)
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
      {farmer.links.map((l) => (
        <Box key={`${l.supplier_id}-${l.fo_id}`} sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5, pl: 1.5, pr: 0.5, py: 0.25,
          borderRadius: `${tokens.radius.sm}px`, backgroundColor: tokens.color.ink100, fontSize: 12,
        }}>
          {[l.supplier, l.fo && `${l.fo}${l.fo_code ? ` (${l.fo_code})` : ''}`].filter(Boolean).join(' · ')}
          <Tooltip title="Remove this link (plots keep their own records)">
            <IconButton size="small" aria-label="Remove link"
              onClick={() => unlinkFarmer.mutateAsync({ id: farmer.id, supplier_id: l.supplier_id, fo_id: l.fo_id }).catch(failed(onError))}>
              <X size={11} strokeWidth={1.5} />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      {!adding ? (
        <IconButton size="small" aria-label={`Add a link for ${farmer.code ?? farmer.name}`} onClick={() => setAdding(true)}>
          <Plus size={13} strokeWidth={1.5} />
        </IconButton>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
          <Box sx={{ flex: 1 }}><SupplierPicker value={supplier} onChange={setSupplier} countryCode={null} /></Box>
          <Box sx={{ flex: 1 }}><FoPicker value={fo} onChange={setFo} supplierId={supplier?.id} /></Box>
          <Button size="small" disabled={(!supplier && !fo) || linkFarmer.isPending}
            onClick={() => linkFarmer.mutateAsync({ id: farmer.id, ...(supplier ? { supplier_id: supplier.id } : {}), ...(fo ? { fo_id: fo.id } : {}) })
              .then(() => { setAdding(false); setSupplier(null); setFo(null) }).catch(failed(onError))}>
            Link
          </Button>
          <Button size="small" variant="text" onClick={() => setAdding(false)}>Cancel</Button>
        </Box>
      )}
    </Box>
  )
}

function AddRow({
  label, disabled, onAdd, children,
}: { label: string; disabled: boolean; onAdd: () => Promise<unknown>; children: React.ReactNode }) {
  return (
    // Above the list, which can run long.
    <Surface sx={{ p: 4, mb: 4 }}>
      <Eyebrow sx={{ mb: 2.5 }}>{label}</Eyebrow>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {children}
        <Button size="small" startIcon={<Plus size={14} strokeWidth={1.5} />} disabled={disabled}
          onClick={() => { onAdd().catch(() => undefined) }}>
          Add
        </Button>
      </Box>
    </Surface>
  )
}
